import {engineContext} from './context.js';

/* ZooInvest Decision Challenge Engine v0.1.0-alpha
 * Engine-only. Turns existing evidence/risk/narrative signals into a short prioritized challenge.
 * No universal score. No BUY/SELL. No Company Quality rewrite.
 * Thresholds PROVISIONAL / UNCALIBRATED.
 */
(function(root,factory){
  const api=factory();
  if(root) root.ZooInvestDecisionChallengeEngine=api;
})(engineContext,function(){
  'use strict';
  const VERSION='0.1.0-alpha';
  const CONFIG=Object.freeze({calibrated:false,thresholds:'PROVISIONAL / UNCALIBRATED',maxChallenges:4});
  const norm=v=>String(v??'').trim().toUpperCase();
  const freeze=o=>Object.freeze(o);

  function collect(input={}){
    const items=[];
    const push=(priority,code,question,reason,source)=>items.push({priority,code,question,reason,source});

    if(input.industryState==='WEAK'||input.industryState==='DISTRESSED')
      push(95,'INDUSTRY_WEAK','The industry is weak. What verified evidence makes this company an exception?','Sector conditions can overwhelm a decent company.','FUNDAMENTALS');

    if(input.profitable===false)
      push(100,'NOT_PROFITABLE','The company is not profitable. What funds the path to profitability?','Losses make runway, dilution and execution matter more.','FUNDAMENTALS');

    if(input.freeCashFlowPositive===false)
      push(90,'FCF_NEGATIVE','Free cash flow is negative. When does cash generation turn positive — and what evidence supports that?','Accounting growth without cash can require more funding.','FUNDAMENTALS');

    if(norm(input.dilutionRisk)==='HIGH')
      push(96,'DILUTION_HIGH','If new capital is required, what happens to your per-share thesis?','A business can improve while shareholders are diluted.','CAPITAL_STRUCTURE');

    if(norm(input.debtRisk)==='HIGH')
      push(94,'DEBT_HIGH','Can the balance sheet survive a slower-than-expected recovery?','Debt reduces room for execution mistakes.','CAPITAL_STRUCTURE');

    if(input.authorityBias===true)
      push(92,'AUTHORITY_BIAS','Would you still want this stock if the expert had never mentioned it?','Authority is metadata, not evidence.','DECISION_SANITY');

    if(input.fomo===true)
      push(91,'FOMO','What changed fundamentally — not just in the price or story?','Urgency can replace analysis.','DECISION_SANITY');

    if(input.chasing===true||input.extended===true)
      push(89,'CHASE','What new evidence justifies paying more now?','A good company can still be a poor entry.','TIMING');

    if(input.oneSidedSources===true)
      push(88,'ONE_SIDED','What is the strongest credible evidence against your thesis?','A thesis that never meets opposition is not stress-tested.','WHY_WRONG');

    if(input.thesisBreakerMissing===true)
      push(87,'NO_THESIS_BREAKER','What specific fact would prove you wrong?','Without a failure condition, conviction can become attachment.','WHY_WRONG');

    if(norm(input.hypePhase)==='BROAD_HYPE'||input.hypePhase===5)
      push(86,'BROAD_HYPE','The story is already everywhere. What is still underappreciated by the market?','Broad attention can compress asymmetry.','NARRATIVE');

    if(norm(input.narrativeGap)==='NARRATIVE_FAR_AHEAD')
      push(93,'NARRATIVE_AHEAD','The narrative is running ahead of fundamentals. Which operating metric catches up first?','Story acceleration without operating confirmation raises expectation risk.','NARRATIVE');

    if(input.eventWithinDays!==undefined && Number.isFinite(input.eventWithinDays) && input.eventWithinDays<=7 && norm(input.eventVerification)!=='VERIFIED')
      push(84,'EVENT_UNVERIFIED','A near-term event matters, but the schedule/evidence is not verified. What exactly is confirmed?','Unverified event timing must not become a catalyst assumption.','EVENT');

    if(input.companySpecificDeterioration===true)
      push(99,'COMPANY_DETERIORATION','Is the weakness still market-driven, or has the company thesis itself deteriorated?','Broad stress must not excuse company-specific damage.','CAUSE_OF_MOVE');

    if(input.purchasePriceAnchor===true)
      push(83,'ANCHORING','If you did not own it already, would you choose it today at this price?','Purchase price and break-even are not investment evidence.','DECISION_PROTECTION');

    return items.sort((a,b)=>b.priority-a.priority);
  }

  function challenge(input={}){
    const all=collect(input);
    const top=all.slice(0,CONFIG.maxChallenges);
    const blockers=top.filter(x=>x.priority>=95);
    const state=blockers.length?'BLOCKED':top.length?'REVIEW':'OPEN';
    return freeze({
      state,
      headline:state==='BLOCKED'?'Stop. Prove the weak points before capital.':state==='REVIEW'?'Challenge the thesis before capital.':'No major decision-process challenge detected.',
      challenges:freeze(top.map(freeze)),
      omittedCount:Math.max(0,all.length-top.length),
      action:state==='BLOCKED'?'INVESTIGATE':top.length?'REVIEW':'NO_ACTION',
      companyQualityEffect:0,
      stockOpportunityOverride:false,
      entryGateOverride:false,
      buySignal:false,
      sellSignal:false,
      universalInvestmentScore:null
    });
  }

  function compressForUI(input={}){
    const r=challenge(input);
    return freeze({
      state:r.state,
      headline:r.headline,
      lines:freeze(r.challenges.map(x=>x.question)),
      more:r.omittedCount,
      destination:'WHY_WRONG',
      note:'Show the few highest-value objections first; details remain expandable.'
    });
  }

  function selfTest(){
    const tests=[],t=(name,pass)=>tests.push({name,pass:!!pass});
    let r=challenge({industryState:'WEAK',profitable:false,authorityBias:true,thesisBreakerMissing:true});
    t('Loss-making weak-industry case is blocked',r.state==='BLOCKED');
    t('Profitability challenge ranks first',r.challenges[0].code==='NOT_PROFITABLE');
    t('Authority challenge is included',r.challenges.some(x=>x.code==='AUTHORITY_BIAS'));
    t('Challenges are compressed to maximum four',r.challenges.length<=4);
    t('No universal score is created',r.universalInvestmentScore===null);
    t('No buy or sell signal is created',r.buySignal===false&&r.sellSignal===false);
    t('Company Quality cannot be rewritten',r.companyQualityEffect===0);
    t('Entry Gate cannot be bypassed',r.entryGateOverride===false);
    r=challenge({purchasePriceAnchor:true});
    t('Purchase-price anchoring triggers fresh-choice question',r.challenges[0].code==='ANCHORING');
    r=compressForUI({hypePhase:5,narrativeGap:'NARRATIVE_FAR_AHEAD'});
    t('UI compression routes to WHY WRONG',r.destination==='WHY_WRONG'&&r.lines.length===2);
    r=challenge({});
    t('Missing flags do not manufacture a warning',r.state==='OPEN'&&r.challenges.length===0);
    r=challenge({companySpecificDeterioration:true,industryState:'WEAK'});
    t('Company deterioration outranks generic industry weakness',r.challenges[0].code==='COMPANY_DETERIORATION');
    return freeze({passed:tests.filter(x=>x.pass).length,total:tests.length,tests});
  }

  const API={VERSION,CONFIG,collect,challenge,compressForUI,selfTest};
  try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
  return freeze(API);
});
